/**
 * Public marketing, auth, legal, and capture-page metadata.
 */

import type { PageMetaEntry } from './types';

/** Marketing homepage title — matches index.html so first paint and SPA stay aligned. */
export const DEFAULT_PUBLIC_TITLE =
  'BuzzBit X — Instagram, WhatsApp & Email Automation for Every Business';

export const DEFAULT_PUBLIC_DESCRIPTION =
  'Marketing automation and CRM for every kind of business. BuzzBit X automates email, books meetings with built-in video calls, and keeps every customer in one record — for shops, clinics, coaches, course creators, SaaS and creators.';

export const PUBLIC_PAGE_META: readonly PageMetaEntry[] = [
  {
    path: '/',
    exact: true,
    title: DEFAULT_PUBLIC_TITLE,
    description: DEFAULT_PUBLIC_DESCRIPTION,
    indexable: true,
    canonicalPath: '/',
  },
  {
    path: '/landing',
    exact: true,
    title: DEFAULT_PUBLIC_TITLE,
    description: DEFAULT_PUBLIC_DESCRIPTION,
    indexable: true,
    canonicalPath: '/',
  },
  {
    path: '/faq',
    exact: true,
    title: 'FAQ',
    description: 'Frequently asked questions about BuzzBit X marketing automation and CRM.',
    indexable: true,
    canonicalPath: '/faq',
  },
  {
    path: '/channels',
    exact: true,
    title: 'Channels',
    description: 'Instagram, WhatsApp, Messenger, email and more — channels BuzzBit X connects.',
    indexable: true,
    canonicalPath: '/channels',
  },
  {
    path: '/flows',
    exact: true,
    title: 'Automation Flows',
    description: 'See how BuzzBit X automation flows turn comments and DMs into customers.',
    indexable: true,
    canonicalPath: '/flows',
  },
  {
    path: '/playbook',
    title: 'Automation Playbook',
    description: 'Free automation playbook with ready-to-use ideas for every kind of business.',
    indexable: true,
    canonicalPath: '/playbook',
  },
  {
    path: '/login',
    exact: true,
    title: 'Sign In',
    description: 'Sign in to your BuzzBit X workspace.',
    indexable: true,
    canonicalPath: '/login',
  },
  {
    path: '/signup',
    exact: true,
    title: 'Create Account',
    description: 'Create a BuzzBit X account and start automating marketing for your business.',
    indexable: true,
    canonicalPath: '/signup',
  },
  {
    path: '/forgot-password',
    exact: true,
    title: 'Forgot Password',
    description: 'Reset your BuzzBit X account password.',
    indexable: false,
  },
  {
    path: '/reset-password',
    exact: true,
    title: 'Reset Password',
    description: 'Choose a new password for your BuzzBit X account.',
    indexable: false,
  },
  {
    path: '/verify-email',
    exact: true,
    title: 'Verify Email',
    description: 'Verify the email address for your BuzzBit X account.',
    indexable: false,
  },
  {
    path: '/onboarding',
    exact: true,
    title: 'Onboarding',
    description: 'Set up your BuzzBit X workspace.',
    indexable: false,
  },
  {
    path: '/getting-started',
    exact: true,
    title: 'Getting Started',
    description: 'Get started with BuzzBit X marketing automation and CRM.',
    indexable: true,
    canonicalPath: '/getting-started',
  },
  {
    path: '/privacy',
    exact: true,
    title: 'Privacy Policy',
    description:
      'BuzzBit X Privacy Policy: what personal data we collect, why, who we share it with, how long we keep it and your rights.',
    indexable: true,
    canonicalPath: '/privacy',
  },
  {
    path: '/terms',
    exact: true,
    title: 'Terms of Service',
    description:
      'BuzzBit X Terms of Service: the agreement between BuzzBit X Ltd. and the businesses that use the platform.',
    indexable: true,
    canonicalPath: '/terms',
  },
  {
    path: '/cookies',
    exact: true,
    title: 'Cookie Policy',
    description:
      'BuzzBit X Cookie Policy: what the web app stores in your browser and which third-party resources it loads.',
    indexable: true,
    canonicalPath: '/cookies',
  },
  {
    path: '/acceptable-use',
    exact: true,
    title: 'Acceptable Use',
    description: 'Acceptable use policy for the BuzzBit X platform.',
    indexable: true,
    canonicalPath: '/acceptable-use',
  },
  {
    path: '/accessibility-statement',
    exact: true,
    title: 'Accessibility',
    description: 'Accessibility statement for the BuzzBit X web application.',
    indexable: true,
    canonicalPath: '/accessibility-statement',
  },
  {
    path: '/docs/claude',
    exact: true,
    title: 'Claude Docs',
    description: 'Use Claude with BuzzBit X via MCP — tools reference and setup.',
    indexable: true,
    canonicalPath: '/docs/claude',
  },
  {
    path: '/docs/mcp',
    exact: true,
    title: 'MCP Docs',
    description: 'BuzzBit X MCP server documentation — 100+ tools for AI clients.',
    indexable: true,
    canonicalPath: '/docs/mcp',
  },
  {
    path: '/docs/plugin',
    exact: true,
    title: 'Plugin Docs',
    description: 'BuzzBit X plugin documentation for AI coding assistants.',
    indexable: true,
    canonicalPath: '/docs/plugin',
  },
  {
    path: '/data-deletion-status',
    exact: true,
    title: 'Data Deletion Status',
    description: 'Check the status of a BuzzBit X data deletion request.',
    indexable: false,
  },
  {
    path: '/data-status',
    exact: true,
    title: 'Data Status',
    description: 'Check the status of a BuzzBit X data request.',
    indexable: false,
  },
  {
    path: '/oauth/consent',
    title: 'Authorize App',
    description: 'Authorize a third-party application to access your BuzzBit X workspace.',
    indexable: false,
  },
  {
    path: '/oauth/authorize',
    title: 'Authorize',
    description: 'Complete OAuth authorization for BuzzBit X.',
    indexable: false,
  },
  {
    path: '/connect',
    exact: true,
    title: 'Device Connect',
    description: 'Connect a device to your BuzzBit X account.',
    indexable: false,
  },
  {
    path: '/invite',
    title: 'Team Invite',
    description: 'Accept a BuzzBit X team invitation.',
    indexable: false,
  },
  {
    path: '/chat/accept',
    title: 'Chat Invite',
    description: 'Accept a BuzzTalk guest invitation.',
    indexable: false,
  },
  {
    path: '/f/',
    title: 'Form',
    description: 'Fill out a BuzzBit X form.',
    indexable: false,
  },
  {
    path: '/e/',
    title: 'Event',
    description: 'Register for an event powered by BuzzBit X.',
    indexable: false,
  },
  {
    path: '/book/',
    title: 'Book Appointment',
    description: 'Book a time slot with this business via BuzzBit X.',
    indexable: false,
  },
  {
    path: '/r/',
    title: 'Booking',
    description: 'Choose how to book with this business via BuzzBit X.',
    indexable: false,
  },
  {
    path: '/booking/',
    title: 'Manage Booking',
    description: 'View or change your booking via BuzzBit X.',
    indexable: false,
  },
  {
    path: '/p/',
    title: 'BuzzPage',
    description: 'Link-in-bio page powered by BuzzBit X.',
    indexable: true,
  },
  {
    path: '/m/',
    title: 'Members',
    description: 'Member lesson page powered by BuzzBit X.',
    indexable: false,
  },
  {
    path: '/pay/',
    title: 'Checkout',
    description: 'Complete payment via BuzzBit X checkout.',
    indexable: false,
  },
  {
    path: '/call/',
    title: 'Video Call',
    description: 'Join a BuzzBit X video call.',
    indexable: false,
  },
  {
    path: '/vendor/',
    title: 'Vendor Dashboard',
    description: 'Vendor dashboard for BuzzBit X partners.',
    indexable: false,
  },
];
